import * as Arr from 'effect/Array';
import * as Brand from 'effect/Brand';
import * as Data from 'effect/Data';
import * as Either from 'effect/Either';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';
import * as P from 'effect/Predicate';
import * as Rec from 'effect/Record';
import * as Str from 'effect/String';
import * as Struct from 'effect/Struct';
import { COUNTRY_CODES } from './country-codes';

/** Array of all country codes. */
const ALL_COUNTRY_CODES = Fn.pipe(COUNTRY_CODES, Rec.values, Arr.dedupe);

type CountryCodeValue = (typeof ALL_COUNTRY_CODES)[0];
/** Country code. */
export type CountryCode = CountryCodeValue & Brand.Brand<'Phone.CountryCode'>;
export const CountryCode = Brand.refined<CountryCode>(
  (code: string) => Arr.contains(ALL_COUNTRY_CODES, code),
  (code) => Brand.error(`Unknown country code ${code}`),
);
/** An unvalidated phone number. */
export interface UnvalidatedPhoneNumber {
  readonly _tag: 'UnvalidatedPhoneNumber';
  readonly countryCode: O.Option<string>;
  readonly phoneNumber: string;
}
export const UnvalidatedPhoneNumber = Data.tagged<UnvalidatedPhoneNumber>(
  'UnvalidatedPhoneNumber',
);
/** A validated phone number. */
export interface PhoneNumber {
  readonly _tag: 'PhoneNumber';
  readonly countryCode: O.Option<CountryCode>;
  readonly phoneNumber: string;
}
export const PhoneNumber = Data.tagged<PhoneNumber>('PhoneNumber');

export const hasPhoneNumber: P.Predicate<UnvalidatedPhoneNumber> = P.struct({
  phoneNumber: Str.isNonEmpty,
});

export function parse(unparsed: string): UnvalidatedPhoneNumber {
  const value = Fn.pipe(unparsed, Str.replace(/\s/g, ''));

  const countryCode = _parseCountryCode(value);
  const phoneNumber = Fn.pipe(
    countryCode,
    O.match({
      onNone: () => O.some(_removeInternationalPrefix(value)),
      onSome: (code) => _parsePhoneNumber(code, value),
    }),
    O.getOrElse(() => ''),
  );

  return UnvalidatedPhoneNumber({
    countryCode,
    phoneNumber,
  });
}

export const formatCountryCode = (code: CountryCode | string): string =>
  `+${code}`;

export const format = (input: UnvalidatedPhoneNumber | PhoneNumber): string =>
  Fn.pipe(
    input,
    Struct.evolve({
      countryCode: (c) =>
        c.pipe(
          O.map(formatCountryCode),
          O.getOrElse(() => ''),
        ),
    }),
    (v) => `${v.countryCode}${v.phoneNumber}`,
  );

/**
 * Validates an unvalidated phone number.
 * @param unvalidated
 * @returns
 */
export const validate = (
  unvalidated: UnvalidatedPhoneNumber,
): Either.Either<PhoneNumber, string> =>
  Fn.pipe(
    unvalidated,
    Either.liftPredicate(hasPhoneNumber, (n) => `Phone number is empty`),
    Either.map(
      Fn.flow(
        Struct.evolve({
          countryCode: O.flatMap((c) =>
            CountryCode.option(c as CountryCodeValue),
          ),
        }),
        PhoneNumber,
      ),
    ),
  );

function _parseCountryCode(value: string): O.Option<CountryCode> {
  return Fn.pipe(
    ALL_COUNTRY_CODES,
    Arr.findFirst(
      (code) => value.match(new RegExp(`^(?:\\+|00)${code}`)) !== null,
    ),
    O.flatMap((c) => CountryCode.option(c)),
  );
}

const _parsePhoneNumber = (
  countryCode: CountryCode,
  value: string,
): O.Option<string> =>
  Fn.pipe(
    value,
    _removeInternationalPrefix,
    Str.substring(countryCode.length),
    O.liftPredicate(Str.isNonEmpty),
  );

const _removeInternationalPrefix = Str.replace(/^(?:\+|00)/, '');
